import { useNotification } from "./useNotification";
import { useTranslation } from "react-i18next";
import { TOAST_VARIANT } from "@root/constants";

/**
 * Custom hook for validating file sizes against a maximum limit and converting to FileList.
 * Automatically shows error notifications for files that exceed the limit.
 * Returns a FileList containing only valid files.
 *
 * @param maxFileSizeMB - Maximum allowed file size in MB
 * @returns A function that validates files and returns a FileList (or null if no valid files)
 *
 * @example
 * const validateFiles = useFileSizeValidation(300);
 * const fileList = validateFiles(inputFiles); // Returns FileList | null
 */
export const useFileSizeValidation = (maxFileSizeMB: number) => {
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");

  const validateFiles = (files: FileList | File[] | null): FileList | null => {
    if (!files) return null;

    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
    const validFiles: File[] = [];

    Array.from(files)?.forEach((file) => {
      if (file?.size > maxFileSizeBytes) {
        showNotification({
          type: TOAST_VARIANT.ERROR,
          message: t("upload_document.fileTooLargeDescription", {
            fileName: file.name,
            maxSize: maxFileSizeMB,
          }),
        });
        return;
      }
      validFiles?.push(file);
    });

    if (validFiles?.length === 0) return null;

    // Convert valid files to FileList
    const dataTransfer = new DataTransfer();
    validFiles?.forEach((file) => dataTransfer?.items?.add(file));
    return dataTransfer?.files;
  };

  return validateFiles;
};
